/**
 * Client state + sync engine. The server is authoritative; the client applies idempotent
 * "upsert" events from the event log (WebSocket, or HTTP long-poll fallback) and keeps an
 * offline outbox keyed by clientMessageId so retries never duplicate messages.
 */
import { useSyncExternalStore } from 'react';
import type { PrivacyInfoDTO, BootstrapDTO, ConversationDTO, EventDTO, MeDTO, MessageDTO, SyncResponseDTO, UserSummaryDTO, WsServerFrame } from '@tc/contracts';
import { uuidv7 } from '@tc/domain';
import { api, ApiError, NetworkError } from './api.ts';
import { setCloudVoices } from './tts.ts';
import { chime, needsAttention, setIconBadge } from './alerts.ts';
import { desktop, notifyDesktop } from './desktop.ts';
import { registerNativePush, sendFeedback, unregisterNativePush } from './native.ts';

export interface OutboxItem {
  clientMessageId: string;
  conversationId: string;
  body: string;
  replyToMessageId: string | null;
  sourceLocale: string | null;
  /** Uploaded voice recording sent with this message. */
  mediaId?: string | null;
  quote?: { text: string; locale: string } | null;
  spoken?: boolean;
  mentions?: string[];
  createdAt: string;
  state: 'sending' | 'failed';
  error?: string;
}

export interface Thread {
  items: MessageDTO[];
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
}

export type ConnState = 'connecting' | 'live' | 'polling' | 'offline';

export interface State {
  phase: 'loading' | 'anon' | 'consent' | 'ready';
  privacy: PrivacyInfoDTO | null;
  /** Language chosen on this device before sign-in (content locale; UI derives from it). */
  nativeLocale: string | null;
  me: MeDTO | null;
  users: Record<string, UserSummaryDTO>;
  conversations: Record<string, ConversationDTO>;
  threads: Record<string, Thread>;
  outbox: OutboxItem[];
  cursor: number;
  conn: ConnState;
  provider: string;
  /** Speech recognition provider; 'disabled' hides the microphone. */
  speech: string;
  /** "Translate text in image" provider; 'disabled' hides the button. */
  imageText: string;
  /** Languages read aloud with a cloud voice. */
  cloudVoices: string[];
  /** conversationId → userId → typing indicator expiry (ms). Ephemeral. */
  typing: Record<string, Record<string, number>>;
  /** Online status of people I share a chat with. */
  presence: Record<string, 'online' | string | null>;
  activeId: string | null;
  /** Message ids whose original is currently shown instead of the translation. */
  showOriginal: Record<string, boolean>;
  /** Message to scroll to and flash (e.g. after clicking a search result). */
  highlight: { id: string; nonce: number } | null;
}

let state: State = {
  phase: 'loading',
  me: null,
  users: {},
  conversations: {},
  threads: {},
  outbox: [],
  cursor: 0,
  conn: 'connecting',
  provider: 'mock',
  speech: 'disabled',
  imageText: 'disabled',
  cloudVoices: [],
  typing: {},
  presence: {},
  activeId: null,
  showOriginal: {},
  highlight: null,
  privacy: null,
  nativeLocale: storedNativeLocale(),
};

function storedNativeLocale(): string | null {
  try {
    return localStorage.getItem('tc.nativeLocale');
  } catch {
    return null;
  }
}

/** Remember the language picked on this device (first screen / login / invite). */
export function setNativeLocale(locale: string): void {
  try {
    localStorage.setItem('tc.nativeLocale', locale);
  } catch {
    /* private mode */
  }
  set({ nativeLocale: locale });
}

const listeners = new Set<() => void>();
function set(patch: Partial<State> | ((s: State) => Partial<State>)): void {
  const p = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...p };
  for (const l of listeners) l();
}
export function getState(): State {
  return state;
}
export function useStore<T>(selector: (s: State) => T): T {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => selector(state),
  );
}

// ---- persistence (per-device conveniences only; server stays authoritative) ------------

function storageGet<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function storageSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota: outbox then lives only in memory */
  }
}
const outboxKey = () => `tc.outbox.${state.me?.id ?? 'anon'}`;
function saveOutbox(outbox: OutboxItem[]): void {
  set({ outbox });
  storageSet(outboxKey(), outbox);
}

// ---- ordering helpers --------------------------------------------------------------------

function cmp(a: MessageDTO, b: MessageDTO): number {
  return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function mergeMessages(existing: MessageDTO[], incoming: MessageDTO[]): MessageDTO[] {
  const map = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) {
    const old = map.get(m.id);
    // Same revision without translation info must not wipe a translation we already have.
    if (old && old.revision === m.revision && !m.translation && old.translation && !m.deletedAt) {
      map.set(m.id, { ...m, translation: old.translation });
    } else {
      map.set(m.id, m);
    }
  }
  return [...map.values()].sort(cmp);
}

// ---- event application -----------------------------------------------------------------

function applyMessage(m: MessageDTO, opts: { live: boolean }): void {
  if (m.senderId && state.typing[m.conversationId]?.[m.senderId]) {
    const { [m.senderId]: _gone, ...rest } = state.typing[m.conversationId]!;
    state = { ...state, typing: { ...state.typing, [m.conversationId]: rest } };
  }
  set((s) => {
    const threads = { ...s.threads };
    const t = threads[m.conversationId];
    const isNew = !t?.items.some((x) => x.id === m.id);
    if (t && t.loaded) threads[m.conversationId] = { ...t, items: mergeMessages(t.items, [m]) };
    const conversations = { ...s.conversations };
    const c = conversations[m.conversationId];
    if (c) {
      const last = c.lastMessage;
      const newer = !last || last.id === m.id || cmp(last, m) < 0;
      const mine = m.senderId === s.me?.id;
      const bumpUnread = opts.live && isNew && !mine && m.kind !== 'system' && !m.deletedAt && s.activeId !== m.conversationId && (!last || cmp(last, m) < 0);
      if (opts.live && isNew && !mine && m.kind !== 'system' && !m.deletedAt && needsAttention(m.conversationId, s.activeId)) {
        chime(!!s.me && (m.mentions ?? []).includes(s.me.id) ? 'mention' : 'message');
        notifyDesktop(m, c, m.senderId ? s.users[m.senderId] : undefined, s.me);
      }
      conversations[m.conversationId] = {
        ...c,
        lastMessage: newer ? m : last,
        updatedAt: newer && last?.id !== m.id ? m.createdAt : c.updatedAt,
        unreadCount: bumpUnread ? c.unreadCount + 1 : c.unreadCount,
        unreadMentions: bumpUnread && !!s.me && (m.mentions ?? []).includes(s.me.id) ? (c.unreadMentions ?? 0) + 1 : (c.unreadMentions ?? 0),
        // Keep pinned copies current (translations arriving, edits); pin/unpin itself comes as a conversation event.
        pinned: (c.pinned ?? []).some((p) => p.id === m.id) ? (c.pinned ?? []).map((p) => (p.id === m.id ? m : p)).filter((p) => p.pinnedAt) : (c.pinned ?? []),
      };
    }
    const outbox = m.clientMessageId ? s.outbox.filter((o) => o.clientMessageId !== m.clientMessageId) : s.outbox;
    if (outbox.length !== s.outbox.length) storageSet(outboxKey(), outbox);
    return { threads, conversations, outbox };
  });
}

function applyEvents(events: EventDTO[]): void {
  for (const e of events) {
    switch (e.type) {
      case 'message.upsert':
        applyMessage(e.data, { live: true });
        break;
      case 'conversation.upsert': {
        set((s) => {
          const users = { ...s.users };
          for (const m of e.data.members) users[m.id] = { ...m };
          return { conversations: { ...s.conversations, [e.data.id]: e.data }, users };
        });
        if (state.activeId === e.data.id && e.data.unreadCount > 0) scheduleMarkRead();
        break;
      }
      case 'conversation.removed':
        set((s) => {
          const conversations = { ...s.conversations };
          const threads = { ...s.threads };
          delete conversations[e.conversationId];
          delete threads[e.conversationId];
          return { conversations, threads, activeId: s.activeId === e.conversationId ? null : s.activeId };
        });
        break;
      case 'me.updated': {
        const before = state.me;
        set({ me: e.data });
        if (before && before.readingLocale !== e.data.readingLocale) void reloadThreads();
        break;
      }
    }
  }
}

function applySync(res: SyncResponseDTO): void {
  applyEvents(res.events);
  if (res.cursor > state.cursor) set({ cursor: res.cursor });
}

// ---- bootstrap / auth ----------------------------------------------------------------------

export async function bootstrap(): Promise<void> {
  try {
    const b = await api<BootstrapDTO>('GET', '/api/bootstrap');
    if (b.consentRequired) {
      sync.stop();
      set({ phase: 'consent', me: b.me, privacy: b.privacy, provider: b.translationProvider });
      return;
    }
    const users: Record<string, UserSummaryDTO> = {};
    for (const u of b.users) users[u.id] = u;
    for (const c of b.conversations) for (const m of c.members) users[m.id] ??= m;
    setCloudVoices(b.cloudVoices ?? []);
    set({
      phase: 'ready',
      me: b.me,
      users,
      conversations: Object.fromEntries(b.conversations.map((c) => [c.id, c])),
      threads: {},
      cursor: b.cursor,
      provider: b.translationProvider,
      speech: b.transcriptionProvider ?? 'disabled',
      imageText: b.imageTextProvider ?? 'disabled',
      cloudVoices: b.cloudVoices ?? [],
      privacy: b.privacy,
    });
    set({ outbox: storageGet<OutboxItem[]>(outboxKey(), []).map((o) => ({ ...o, state: 'sending' as const })) });
    sync.start();
    void flushOutbox();
    void registerNativePush(api);
    if (pendingOpenUrl) {
      const url = pendingOpenUrl;
      pendingOpenUrl = null;
      openFromUrl(url);
    }
    const fromHash = location.hash.startsWith('#c=') ? location.hash.slice(3) : null;
    if (fromHash && b.conversations.some((c) => c.id === fromHash)) void openConversation(fromHash);
    // Opened from a notification (/?c=…): open that chat and tidy the address bar.
    const fromQuery = new URLSearchParams(location.search).get('c');
    if (fromQuery) {
      history.replaceState(null, '', location.pathname + location.hash);
      if (b.conversations.some((c) => c.id === fromQuery)) void openConversation(fromQuery);
    }
    void import('./push.ts').then((p) => p.registerServiceWorker());
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) set({ phase: 'anon' });
    else {
      // Server unreachable: retry, keep showing the loading screen.
      set({ conn: 'offline' });
      setTimeout(() => void bootstrap(), 3000);
    }
  }
}

export async function acceptNotice(noticeVersion: string): Promise<void> {
  await api('POST', '/api/me/consent', { noticeVersion, consentProcessing: true, consentCrossBorder: true });
  await bootstrap();
}

export async function deleteAccount(password: string): Promise<void> {
  await api('POST', '/api/me/delete', { password });
  sync.stop();
  try {
    localStorage.removeItem(outboxKey());
  } catch {
    /* ignore */
  }
  set({ phase: 'anon', me: null, conversations: {}, threads: {}, outbox: [], activeId: null, users: {} });
}

// A changed privacy notice (new processor, location …) makes the server answer consent_required.
if (typeof window !== 'undefined') window.addEventListener('tc:consent-required', () => void bootstrap());

/** Returns a ticket when the account uses two-step sign-in (then call `loginWithCode`). */
export async function login(email: string, password: string): Promise<{ mfaTicket: string } | null> {
  const r = await api<{ ok: boolean; mfaRequired?: boolean; ticket?: string }>('POST', '/api/auth/login', { email, password });
  if (r.mfaRequired && r.ticket) return { mfaTicket: r.ticket };
  await bootstrap();
  return null;
}

export async function loginWithCode(ticket: string, code: string): Promise<void> {
  await api('POST', '/api/auth/mfa', { ticket, code });
  await bootstrap();
}

/** Re-read who I can see (people who joined after this page loaded, e.g. a new colleague). */
export async function refreshUsers(): Promise<void> {
  try {
    const list = await api<UserSummaryDTO[]>('GET', '/api/users');
    const users = { ...state.users };
    for (const u of list) users[u.id] = u;
    set({ users });
  } catch {
    /* keep what we have */
  }
}

export async function logout(everywhere = false): Promise<void> {
  // The sign-in screen then waits for a tap instead of asking for Face ID straight away.
  try {
    sessionStorage.setItem('tc.signedOut', '1');
  } catch {
    /* ignore */
  }
  await unregisterNativePush(api);
  await api('POST', everywhere ? '/api/auth/logout-all' : '/api/auth/logout').catch(() => {});
  sync.stop();
  set({ phase: 'anon', me: null, conversations: {}, threads: {}, outbox: [], activeId: null, users: {} });
}

function onUnauthorized(): void {
  sync.stop();
  set({ phase: 'anon', me: null });
}

// ---- conversations & history ------------------------------------------------------------------

export async function openConversation(id: string | null): Promise<void> {
  set({ activeId: id });
  history.replaceState(null, '', id ? `#c=${id}` : location.pathname);
  if (!id) return;
  const t = state.threads[id];
  if (!t?.loaded) await loadOlder(id, true);
  scheduleMarkRead();
}

/**
 * Open a conversation at a specific message: load older pages until it is present (bounded),
 * then ask the thread view to scroll to it and flash it.
 */
export async function jumpToMessage(conversationId: string, messageId: string): Promise<boolean> {
  if (state.activeId !== conversationId) await openConversation(conversationId);
  for (let i = 0; i < 60; i++) {
    const t = state.threads[conversationId];
    if (t?.items.some((m) => m.id === messageId)) break;
    if (t?.loaded && !t.hasMore) break;
    await loadOlder(conversationId, !t?.loaded);
  }
  const found = !!state.threads[conversationId]?.items.some((m) => m.id === messageId);
  if (found) set({ highlight: { id: messageId, nonce: Date.now() } });
  return found;
}

export async function loadOlder(id: string, initial = false): Promise<void> {
  const t = state.threads[id] ?? { items: [], hasMore: true, loading: false, loaded: false };
  if (t.loading || (!initial && !t.hasMore)) return;
  set((s) => ({ threads: { ...s.threads, [id]: { ...t, loading: true } } }));
  try {
    const before = initial ? undefined : t.items[0]?.id;
    const res = await api<{ messages: MessageDTO[]; hasMore: boolean }>('GET', `/api/conversations/${id}/messages?limit=50${before ? `&before=${before}` : ''}`);
    set((s) => {
      const cur = s.threads[id] ?? t;
      return { threads: { ...s.threads, [id]: { items: mergeMessages(cur.items, res.messages), hasMore: res.hasMore, loading: false, loaded: true } } };
    });
  } catch (err) {
    set((s) => ({ threads: { ...s.threads, [id]: { ...(s.threads[id] ?? t), loading: false } } }));
    if (err instanceof ApiError && err.status === 401) onUnauthorized();
  }
}

async function reloadThreads(): Promise<void> {
  set({ threads: {} });
  if (state.activeId) await loadOlder(state.activeId, true);
}

let markTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleMarkRead(): void {
  if (markTimer) clearTimeout(markTimer);
  markTimer = setTimeout(() => {
    const id = state.activeId;
    if (!id || document.visibilityState !== 'visible') return;
    const c = state.conversations[id];
    const items = state.threads[id]?.items ?? [];
    const last = [...items].reverse().find((m) => m.senderId !== state.me?.id);
    if (!c || !last || c.unreadCount === 0) return;
    set((s) => ({ conversations: { ...s.conversations, [id]: { ...c, unreadCount: 0, unreadMentions: 0 } } }));
    api('POST', `/api/conversations/${id}/read`, { messageId: last.id }).catch(() => {});
  }, 400);
}

export async function createConversation(body: { type: 'dm'; userId: string } | { type: 'group'; title: string; memberIds: string[]; allowMixedOrgs?: boolean }): Promise<string> {
  const c = await api<ConversationDTO>('POST', '/api/conversations', body);
  set((s) => ({ conversations: { ...s.conversations, [c.id]: c } }));
  await openConversation(c.id);
  return c.id;
}

export function upsertConversation(c: ConversationDTO): void {
  set((s) => ({ conversations: { ...s.conversations, [c.id]: c } }));
}

// ---- sending (offline outbox) --------------------------------------------------------------------

export function send(
  conversationId: string,
  body: string,
  replyToMessageId: string | null,
  sourceLocale: string | null,
  mediaId: string | null = null,
  quote: { text: string; locale: string } | null = null,
  spoken = false,
  mentions: string[] = [],
): void {
  sendFeedback();
  const item: OutboxItem = {
    clientMessageId: uuidv7(),
    conversationId,
    body,
    replyToMessageId,
    sourceLocale,
    mediaId,
    quote: replyToMessageId ? quote : null,
    spoken,
    mentions,
    createdAt: new Date().toISOString(),
    state: 'sending',
  };
  saveOutbox([...state.outbox, item]);
  void flushOutbox();
}

let flushing = false;
let flushRetry: ReturnType<typeof setTimeout> | null = null;
export async function flushOutbox(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    for (const item of state.outbox.filter((o) => o.state === 'sending')) {
      try {
        const m = await api<MessageDTO>('POST', `/api/conversations/${item.conversationId}/messages`, {
          clientMessageId: item.clientMessageId,
          body: item.body,
          replyToMessageId: item.replyToMessageId,
          sourceLocale: item.sourceLocale,
          ...(item.mediaId ? { mediaId: item.mediaId } : {}),
          ...(item.quote ? { quote: item.quote } : {}),
          ...(item.spoken ? { spoken: true } : {}),
          ...(item.mentions?.length ? { mentions: item.mentions } : {}),
        });
        applyMessage(m, { live: false });
      } catch (err) {
        if (err instanceof NetworkError || (err instanceof ApiError && (err.status >= 500 || err.status === 429))) {
          // Transient: keep it queued; retry later. clientMessageId makes this safe.
          if (flushRetry) clearTimeout(flushRetry);
          flushRetry = setTimeout(() => void flushOutbox(), 5000);
          break;
        }
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          break;
        }
        const code = err instanceof ApiError ? err.code : 'error';
        saveOutbox(state.outbox.map((o) => (o.clientMessageId === item.clientMessageId ? { ...o, state: 'failed', error: code } : o)));
      }
    }
  } finally {
    flushing = false;
  }
}

export function retryOutbox(clientMessageId: string): void {
  saveOutbox(state.outbox.map((o) => (o.clientMessageId === clientMessageId ? { ...o, state: 'sending', error: undefined } : o)));
  void flushOutbox();
}

export function discardOutbox(clientMessageId: string): void {
  saveOutbox(state.outbox.filter((o) => o.clientMessageId !== clientMessageId));
}

// ---- message actions ----------------------------------------------------------------------------------

export async function messageAction(method: string, url: string, body?: unknown): Promise<void> {
  const m = await api<MessageDTO>(method, url, body);
  applyMessage(m, { live: false });
}

export function toggleOriginal(id: string): void {
  set((s) => ({ showOriginal: { ...s.showOriginal, [id]: !s.showOriginal[id] } }));
}

export function setMe(me: MeDTO): void {
  const before = state.me;
  set({ me });
  if (before && before.readingLocale !== me.readingLocale) void reloadThreads();
}

// ---- sync engine: WebSocket first, long-poll fallback -----------------------------------------------------

class SyncEngine {
  private ws: WebSocket | null = null;
  private running = false;
  private failures = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pollAbort: AbortController | null = null;
  private mode: 'ws' | 'poll' = 'ws';
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.running) return;
    this.running = true;
    this.failures = 0;
    this.mode = 'ws';
    window.addEventListener('online', this.kick);
    document.addEventListener('visibilitychange', this.onVisible);
    this.connect();
  }

  stop(): void {
    this.running = false;
    window.removeEventListener('online', this.kick);
    document.removeEventListener('visibilitychange', this.onVisible);
    this.teardown();
  }

  private teardown(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pollAbort?.abort();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private readonly kick = () => {
    if (!this.running) return;
    this.teardown();
    this.failures = 0;
    this.mode = 'ws';
    this.connect();
    void flushOutbox();
  };

  private lastTyping = 0;
  /** "I'm typing" to the other members (WebSocket only; at most every 2.5 s). */
  typing(conversationId: string): void {
    if (Date.now() - this.lastTyping < 2500 || this.ws?.readyState !== WebSocket.OPEN) return;
    this.lastTyping = Date.now();
    this.ws.send(JSON.stringify({ type: 'typing', conversationId }));
  }

  private readonly onVisible = () => {
    const visible = document.visibilityState === 'visible';
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'visibility', visible }));
    if (!visible) return;
    // Mobile browsers freeze sockets in the background; reconnect and resync on return.
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) this.kick();
    scheduleMarkRead();
  };

  private connect(): void {
    if (!this.running) return;
    if (this.mode === 'poll') return void this.poll();
    set({ conn: state.conn === 'live' ? 'connecting' : state.conn === 'offline' ? 'offline' : 'connecting' });
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    let opened = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${proto}://${location.host}/api/ws`);
    } catch {
      return this.scheduleReconnect(false);
    }
    this.ws = ws;
    const openTimeout = setTimeout(() => {
      if (!opened) ws.close();
    }, 8000);
    ws.onopen = () => {
      opened = true;
      clearTimeout(openTimeout);
      ws.send(JSON.stringify({ type: 'hello', cursor: state.cursor }));
      // Tell the server whether the app is in front, so it can push to me only when I'm away.
      if (document.visibilityState !== 'visible') ws.send(JSON.stringify({ type: 'visibility', visible: false }));
      this.pingTimer = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'ping' })), 25_000);
    };
    ws.onmessage = (ev) => {
      let frame: WsServerFrame;
      try {
        frame = JSON.parse(String(ev.data)) as WsServerFrame;
      } catch {
        return;
      }
      if (frame.type === 'events') {
        this.failures = 0;
        set({ conn: 'live' });
        applySync(frame);
      } else if (frame.type === 'typing') {
        const until = Date.now() + 4500;
        set((st) => ({ typing: { ...st.typing, [frame.conversationId]: { ...(st.typing[frame.conversationId] ?? {}), [frame.userId]: until } } }));
        setTimeout(() => set((st) => ({ typing: { ...st.typing } })), 4600); // re-render to expire
      } else if (frame.type === 'error' && frame.code === 'unauthenticated') {
        onUnauthorized();
      }
    };
    ws.onclose = () => {
      clearTimeout(openTimeout);
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.ws = null;
      this.scheduleReconnect(opened);
    };
  }

  private scheduleReconnect(wasOpen: boolean): void {
    if (!this.running) return;
    this.failures = wasOpen ? 1 : this.failures + 1;
    // Some networks block or break WebSockets; after repeated failures fall back to long-poll,
    // and periodically try WebSockets again.
    if (this.failures >= 3) {
      this.mode = 'poll';
      this.timer = setTimeout(() => {
        this.mode = 'ws';
        this.failures = 0;
        this.teardown();
        this.connect();
      }, 120_000);
      return void this.poll();
    }
    set({ conn: navigator.onLine ? 'connecting' : 'offline' });
    const delay = Math.min(1000 * 2 ** (this.failures - 1), 15_000) + Math.random() * 500;
    this.timer = setTimeout(() => this.connect(), delay);
  }

  private async poll(): Promise<void> {
    let errors = 0;
    while (this.running && this.mode === 'poll') {
      this.pollAbort = new AbortController();
      try {
        const res = await api<SyncResponseDTO>('GET', `/api/sync?after=${state.cursor}&wait=25`, undefined, { signal: this.pollAbort.signal, timeoutMs: 35_000 });
        errors = 0;
        set({ conn: 'polling' });
        applySync(res);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        if (!this.running || this.mode !== 'poll') return;
        errors++;
        set({ conn: 'offline' });
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** errors, 30_000)));
      }
    }
  }
}

export const sync = new SyncEngine();

// ---- unread count in the tab title; deep links (?c=…) from notifications -----------------------

const BASE_TITLE = document.title || 'T-Lián';
listeners.add(() => {
  const n = Object.values(state.conversations).reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
  const title = n > 0 ? `(${n}) ${BASE_TITLE}` : BASE_TITLE;
  if (document.title !== title) document.title = title;
  setIconBadge(state.phase === 'ready' ? n : 0);
});

/** A notification tapped before the chats were loaded (app cold start) is opened once they are. */
let pendingOpenUrl: string | null = null;

export function openFromUrl(url: string): void {
  if (state.phase !== 'ready') {
    pendingOpenUrl = url;
    return;
  }
  try {
    const params = new URL(url, location.origin).searchParams;
    if (params.get('admin') === 'users') window.dispatchEvent(new Event('tc-open-admin-users'));
    const id = params.get('c');
    if (id && state.conversations[id]) void openConversation(id);
  } catch {
    /* ignore */
  }
}

// Desktop app: clicking a native notification opens that chat.
desktop?.onOpenUrl((url) => openFromUrl(url));

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (e) => {
    if ((e.data as { type?: string })?.type === 'open') openFromUrl((e.data as { url: string }).url);
  });
}

export function sendTyping(conversationId: string): void {
  sync.typing(conversationId);
}

// ---- presence ("active now" / last seen), refreshed every 30 s while the app is visible -----------
let presenceTimer: ReturnType<typeof setInterval> | null = null;
export async function refreshPresence(): Promise<void> {
  if (state.phase !== 'ready' || document.visibilityState !== 'visible') return;
  try {
    set({ presence: await api<Record<string, 'online' | string | null>>('GET', '/api/presence') });
  } catch {
    /* ignore */
  }
}
listeners.add(() => {
  if (state.phase === 'ready' && !presenceTimer) {
    presenceTimer = setInterval(() => void refreshPresence(), 30_000);
    void refreshPresence();
  } else if (state.phase !== 'ready' && presenceTimer) {
    clearInterval(presenceTimer);
    presenceTimer = null;
  }
});
